import type React from "react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import CheckIcon from "@mui/icons-material/Check";
import CreditCardOffOutlinedIcon from "@mui/icons-material/CreditCardOffOutlined";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { common } from "@mui/material/colors";
import useTheme from "@mui/material/styles/useTheme";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { misc, neutral } from "app/themes/colors";
import {
  setCustomMessage as setSnackbarMessage,
  setOpen as setSnackbarOpen
} from "components/action-snackbar/action-snackbar-slice";
import CircularProgress from "components/circular-progress";
import type { ConfirmDialogOptions } from "components/confirm-dialog/confirm-dialog.component";
import {
  setContent as setConfirmDialogContent,
  setOnChange as setConfirmDialogOnChange,
  setOpen as setConfirmDialogOpen,
  setOptions as setConfirmDialogOptions,
  setTitle as setConfirmDialogTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import { AMBIENT_SCRIBE_FEATURE_KEY } from "constants/paywall";
import {
  selectCurrentPractice,
  selectFeatures,
  updateCurrentPractice
} from "features/auth/auth-slice";
import usePurchasePaywallFeature from "features/paywall/use-purchase-paywall-feature.hook";
import { glowShadowGreenButtonSx } from "features/paywall/utils";
import { useGetAmbientScribePurchaseSummaryQuery } from "services/paywall";
import { useLazyGetPracticeStripeCustomerQuery } from "services/practices";
import type { PaywallFeature } from "services/types/paywall.type";
import { formatMoney } from "utils/common";

import useAmbientScribeNoCardTrial from "../../hooks/use-ambient-scribe-no-card-trial.hook";
import { useScribePurchaseInProgress } from "../../hooks/use-scribe-purchase-marker.hook";
import useAmbientScribeTrialStatus from "../../use-ambient-scribe-trial-status.hook";
import TrialStatusBanner from "../trial-status-banner.component";
import {
  AMBIENT_SCRIBE_DEMO_EMBED_URL,
  AMBIENT_SCRIBE_DEMO_SHARE_URL,
  AMBIENT_SCRIBE_PLACEHOLDER_BULLETS,
  AMBIENT_SCRIBE_PLACEHOLDER_COPY as COPY,
  PURCHASE_LICENSES_COPY
} from "./ambient-scribe-purchase-placeholder.copy";
import {
  MAX_LICENSES,
  MIN_LICENSES,
  clampLicenseCountOnBlur,
  isValidLicenseCount
} from "./license-count.util";
import PurchaseHelpContent, {
  buildPurchaseHelpMailto
} from "./purchase-help-content.component";
import PurchaseLicensesDialog from "./purchase-licenses-dialog.component";

export { MAX_LICENSES, MIN_LICENSES };

const LICENSES_INPUT_LABEL_ID = "ambient-scribe-licenses-label";

/**
 * Layout is deliberately CONTAINER-driven (CSS grid `auto-fit`), never viewport-driven
 * (`useMediaQuery`). This component renders in three hosts whose widths differ wildly at
 * the same viewport — the Scribes page section, the patient Scribes tab (inset inside an
 * already-inset content area) and the purchase dialog's paper. A viewport query would
 * report "desktop" and lay two columns inside a 600px dialog.
 *
 * `min(100%, Npx)` is what keeps a track from overflowing a container narrower than N.
 *
 * The 500px minimum is ARITHMETIC, not taste, and it is what caps the grid at two tracks.
 * `auto-fit` fits as many tracks as it can, and both grids have exactly two children — so
 * a third track is always empty dead space. With the root capped at MAX_CONTENT_WIDTH:
 *
 *   3 tracks need 3x500 + 2xgap = 1596px (hero) > 1560px  -> never fits
 *   2 tracks need 2x500 + 1xgap = 1048px                  -> fits from ~1048px up
 *
 * So raising MAX_CONTENT_WIDTH without raising this minimum silently reintroduces a third
 * column. `tests/repro/sn-19608-purchase-placeholder.spec.ts` asserts <= 2 tracks so that
 * change fails a test instead of shipping.
 */
const MAX_CONTENT_WIDTH = 1560;

/**
 * Container width at which a fluid size reaches its maximum. Every clamp below therefore
 * saturates by 1400px, and that is what makes `vw` acceptable despite this component having
 * three differently-sized hosts: past saturation the page (container 1560) and the dialog
 * paper (1240) render IDENTICAL sizes, so the host difference only exists inside a narrow
 * band and amounts to a few percent of a modest size.
 *
 * `cqi` + `container-type: inline-size` would be more precise, but it would be the repo's
 * first container query, it puts a containment/stacking context on the root, and an
 * unsupported `cqi` invalidates the whole `font-size` declaration — dropping a 46px heading
 * to the inherited 16px. That is a worse failure than a few percent of size. This is NOT
 * inconsistent with the grids being container-driven: there, one-vs-two columns is binary
 * and dramatic across containers ranging 600->1560px.
 */
const FLUID_TYPE_MAX_AT_PX = 1400;

/** `fluidType(15, 17.5)` reads 15px in a narrow container and 17.5px from 1400px up. */
const fluidType = (minPx: number, maxPx: number) =>
  `clamp(${minPx}px, ${((maxPx / FLUID_TYPE_MAX_AT_PX) * 100).toFixed(3)}vw, ${maxPx}px)`;

/** Hero: copy beside the video. */
const HERO_GRID = "repeat(auto-fit, minmax(min(100%, 500px), 1fr))";

/**
 * Purchase band: copy beside the controls. Its minimum is 700px, not 500px, because the
 * control row is the widest indivisible thing on the screen — CTA + licences + price +
 * gaps measures ~672px. At a 500px minimum the band went two-up from ~1048px, handing the
 * controls only ~600px, and the price box wrapped onto a second line under the CTA. So the
 * band stays one column until two 700px tracks fit, i.e. until the controls get room for
 * the whole row.
 *
 *   3 tracks: 3x700 + 2x40 = 2180px > 1504px (1560 less the band's own padding) -> never
 *   2 tracks: 2x700 + 1x40 = 1440px                                             -> ~1920px viewport up
 */
const BAND_GRID = "repeat(auto-fit, minmax(min(100%, 700px), 1fr))";

/**
 * With a trial on offer the control row also carries the no-card note: CTA + price + note +
 * gaps measures ~1010px, which a two-up band never has (its controls column is ~730px at a
 * 1920px viewport), so the note would always drop under the CTA. The band stays one column
 * instead: the copy on top, the whole control row beneath it.
 */
const TRIAL_BAND_GRID = "minmax(0, 1fr)";

const UPPERCASE_LABEL_SX = {
  fontSize: fluidType(14, 15),
  fontWeight: 800,
  lineHeight: 1.3,
  letterSpacing: "0.7px",
  textTransform: "uppercase",
  // neutral[700] (#7A807E) is the design's value but measures ~4.0:1 on white,
  // under the 4.5:1 AA floor for normal text. neutral[800] is ~14:1.
  color: neutral[800]
} as const;

const FIELD_LABEL_SX = {
  fontSize: fluidType(15, 17),
  fontWeight: 700,
  lineHeight: 1.3,
  color: neutral[900]
} as const;

const PRICE_BOX_SX = {
  background: "rgba(15, 136, 202, 0.07)",
  borderRadius: "8px",
  padding: "8px 16px",
  minWidth: "155px",
  minHeight: "74px"
} as const;

interface CtaCopyInput {
  offer: "start" | null;
  trialDays: number | null;
  /** The trial without a card applies, whether or not the backend could start it. */
  isNoCardTrial: boolean;
  /** The card checkout charges at once, so it must never be worded as a trial (D12). */
  chargesAtOnce: boolean;
}

function ctaLabel({
  offer,
  trialDays,
  isNoCardTrial,
  chargesAtOnce
}: CtaCopyInput) {
  if (offer === "start" && trialDays) return COPY.ctaNoCardTrial(trialDays);
  if (isNoCardTrial) return COPY.ctaNoCardTrialUnavailable;
  return chargesAtOnce ? COPY.ctaPurchase : COPY.ctaTrial;
}

/** `null` where there is no trial to promise: it was used, or the backend refused one. */
function trialBandLeadIn({
  offer,
  trialDays,
  isNoCardTrial,
  chargesAtOnce
}: CtaCopyInput) {
  if (offer === "start" && trialDays)
    return COPY.trialBandLeadInDays(trialDays);
  if (isNoCardTrial || chargesAtOnce) return null;
  return COPY.trialBandLeadIn;
}

export interface AmbientScribePurchasePlaceholderProps {
  /**
   * Supplies the price and the purchase target. Optional because the dialog's
   * paywall query is `skip`-gated on the dialog being open — and because a failed
   * query must not produce an enabled CTA that silently does nothing.
   */
  readonly feature?: PaywallFeature;
  /**
   * The host's own FIRST-LOAD state. Never pass `isFetching`: that is true on every
   * refetch, which blanks the whole screen when an unrelated tag invalidation lands.
   */
  readonly isLoading?: boolean;
  readonly showTrialStatusBanner?: boolean;
  /**
   * Dialog only. The purchase dialog sits at `snackbar + 101`, so anything this component
   * opens on top of it — the CTA tooltip's popper, and the support modal — has to be raised
   * above that. Left undefined (the page hosts), the tooltip passes no `PopperProps` and the
   * confirm dialog keeps its own `snackbar + 2` default, which nothing there competes with.
   */
  readonly nestedLayerZIndex?: number;
  /**
   * Called once a trial without a card has started (SN-19868), so a page host can swap this
   * screen for Scribe. Resolves with whether Scribe is now available: `false` means the screen
   * stays, so it asks for a reload. The dialog host needs nothing: starting the trial closes it.
   * Also called when a purchase this screen was waiting on goes live (back from Stripe).
   */
  readonly onTrialStarted?: () => Promise<boolean>;
}

export default function AmbientScribePurchasePlaceholder({
  feature,
  isLoading = false,
  showTrialStatusBanner = false,
  nestedLayerZIndex,
  onTrialStarted
}: AmbientScribePurchasePlaceholderProps) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const currentPractice = useAppSelector(selectCurrentPractice);
  const { purchasePaywallFeature, isCreatingFeatureSubscription } =
    usePurchasePaywallFeature();
  const { isTrialExpired, hasHadNoCardTrial, isLoadingSubscriptions } =
    useAmbientScribeTrialStatus();
  const {
    isFlagOn,
    offer,
    trialDays,
    offerError,
    isLoadingOffer,
    startTrial,
    finishTrialStart,
    isStarting
  } = useAmbientScribeNoCardTrial();
  /**
   * The trial without a card replaces the purchase controls wherever the backend offers it — and
   * also where it refused with a reason (`offerError`), so the reason shows on a disabled button
   * instead of a checkout the practice was never meant to see. A `null` offer is the card flow.
   */
  const isNoCardTrial = offer !== null || offerError !== null;
  /**
   * The card checkout charges at once: the trial lapsed, the practice had a trial without a card
   * (checkout then skips Stripe's trial), or the flag is on and the backend offered no trial. Only
   * then does it need a Stripe customer up front.
   */
  const chargesAtOnce = isFlagOn || isTrialExpired || hasHadNoCardTrial;
  // Keeps the CTA disabled from a successful start until the host swaps this screen out.
  // `needs-reload`: the host re-read access and found none, so nothing will swap it.
  const [trialStart, setTrialStart] = useState<
    "not-started" | "started" | "needs-reload"
  >("not-started");

  const [licenses, setLicenses] = useState<string>(String(MIN_LICENSES));

  const licenseCount = Number(licenses);
  const hasValidLicenseCount = isValidLicenseCount(licenseCount);
  // With the flag on there is no licence field on the screen: the count is chosen in the purchase
  // dialog, and the band shows the price of one licence.
  const purchaseLicenses = isFlagOn ? 1 : licenseCount;
  const hasValidLicenses = isFlagOn || hasValidLicenseCount;

  /**
   * An out-of-range value is surfaced by the disabled CTA and its visible reason until the
   * field is left, and only then clamped (see `clampLicenseCountOnBlur`).
   */
  const handleLicensesBlur = useCallback(() => {
    setLicenses(clampLicenseCountOnBlur(licenses));
  }, [licenses]);

  /**
   * A wheel gesture over a focused number input changes its value in Chrome. Blurring is
   * the standard guard — `preventDefault` is unavailable because React registers wheel
   * listeners as passive.
   */
  const handleLicensesWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      (event.target as HTMLElement).blur();
    },
    []
  );

  /**
   * Both of this screen's modals go through the shared confirm dialog. Its action button always
   * fires `onChange`, so an acknowledge-only modal passes a no-op. The `zIndex` is not optional
   * inside the purchase dialog — the confirm dialog defaults to `snackbar + 2`, below that
   * dialog's `snackbar + 101`, and would paint behind it.
   */
  const openModal = useCallback(
    (
      title: string,
      content: ReactNode,
      options: ConfirmDialogOptions,
      onAction: () => void = () => undefined
    ) => {
      dispatch(setConfirmDialogTitle(title));
      dispatch(setConfirmDialogContent(content));
      dispatch(
        setConfirmDialogOptions({
          ...options,
          ...(nestedLayerZIndex !== undefined && { zIndex: nestedLayerZIndex })
        })
      );
      dispatch(setConfirmDialogOnChange(onAction));
      dispatch(setConfirmDialogOpen(true));
    },
    [dispatch, nestedLayerZIndex]
  );

  /**
   * A 400 from the purchase or trial endpoint means the practice cannot go ahead for a reason
   * only Suno support can clear, so it is shown as information rather than as an error snackbar
   * that fades. Every other failure is already handled inside the hooks.
   */
  const openSupportModal = useCallback(
    ({
      message,
      subscriptionId
    }: {
      message: string;
      subscriptionId?: number;
    }) => {
      openModal(
        COPY.supportModalTitle,
        <Stack sx={{ gap: 1.5 }}>
          <Typography sx={{ fontSize: fluidType(15, 17.5), lineHeight: 1.6 }}>
            {message}
          </Typography>
          <Typography sx={{ fontSize: fluidType(15, 17.5), lineHeight: 1.6 }}>
            {COPY.supportModalBody}
          </Typography>
          {subscriptionId !== undefined && (
            <Typography
              sx={{ fontSize: fluidType(14, 15), color: neutral[800] }}
            >
              {COPY.supportModalReference(subscriptionId)}
            </Typography>
          )}
        </Stack>,
        {
          actionButtonText: COPY.supportModalAcknowledge,
          hideCancelButton: true
        }
      );
    },
    [openModal]
  );

  /**
   * After the backend started a trial, by either path. The CTA is disabled before the features
   * refetch, so it cannot be clicked again while that runs, and stays disabled until the host
   * swaps this screen out.
   */
  const completeTrialStart = useCallback(async () => {
    setTrialStart("started");
    await finishTrialStart();
    const isScribeAvailable = await onTrialStarted?.();
    if (isScribeAvailable === false) setTrialStart("needs-reload");
  }, [finishTrialStart, onTrialStarted]);

  // `practice` starts as null, so reading stripe_customer_id off it before auth resolves
  // would send everyone to the "How to purchase" modal.
  const isPracticeResolved = Boolean(currentPractice?.id);

  /**
   * With the flag on, the checkout screen reads what a purchase would do before the CTA is
   * clicked: whether checkout would refuse (then only support can sell Scribe) and the practice's
   * own price, which support may have set. The dialog asks again when it opens. Never with the
   * flag off, and never while a trial is on offer, where the CTA starts the trial instead.
   */
  const shouldReadSummary =
    isFlagOn && !isLoadingOffer && !isNoCardTrial && isPracticeResolved;
  const {
    data: summaryData,
    isError: isSummaryError,
    refetch: refetchSummaryQuery
  } = useGetAmbientScribePurchaseSummaryQuery(undefined, {
    skip: !shouldReadSummary
  });
  // Gated on the same condition as `skip`, and dropped on an error: RTK keeps the last `data` both
  // when a query is skipped and when a refetch fails.
  const summary =
    shouldReadSummary && !isSummaryError ? summaryData : undefined;
  const purchaseRefusal = summary?.purchase_refusal ?? null;
  const unitPrice = summary ? summary.unit_price : feature?.price;
  const hasPrice = Number(unitPrice) > 0;
  // RTK throws on refetching a skipped query.
  const refetchSummary = useCallback(() => {
    if (shouldReadSummary) refetchSummaryQuery();
  }, [refetchSummaryQuery, shouldReadSummary]);

  // The licence-count dialog the CTA opens with the flag on. A failure carries the sentence the
  // dialog shows.
  const [isLicensesDialogOpen, setIsLicensesDialogOpen] = useState(false);
  const [licensesPurchase, setLicensesPurchase] = useState<
    "idle" | "busy" | { failure: string }
  >("idle");
  // A ref as well as the state: a double click lands twice before the busy render.
  const isPurchasingRef = useRef(false);

  // While the screen re-reads what a "contact Suno support" answer rests on (`recheckBeforeSupport`).
  const [readStripeCustomer] = useLazyGetPracticeStripeCustomerQuery();
  const [isRecheckingSupport, setIsRecheckingSupport] = useState(false);
  const isRecheckingSupportRef = useRef(false);

  /**
   * One checkout, from either the inline count (flag off) or the dialog (flag on). A result the
   * practice cannot act on closes the dialog first, so the support modal is not behind it.
   */
  const purchase = useCallback(
    async (count: number) => {
      if (!feature) return undefined;

      const result = await purchasePaywallFeature(feature, count);
      if (result.outcome === "needs-support") {
        setIsLicensesDialogOpen(false);
        openSupportModal(result);
      } else if (result.outcome === "trial-started") {
        // No usable Stripe customer, so the backend started the trial without a card instead.
        setIsLicensesDialogOpen(false);
        await completeTrialStart();
      }
      return result;
    },
    [completeTrialStart, feature, openSupportModal, purchasePaywallFeature]
  );

  const handlePurchase = useCallback(async () => {
    if (!hasValidLicenses) return;
    const result = await purchase(purchaseLicenses);
    // Support changed the practice's billing mid-request: nothing was charged, and a retry works.
    if (result?.outcome === "conflict") {
      dispatch(setSnackbarMessage(result.message));
      dispatch(setSnackbarOpen(true));
    }
  }, [dispatch, hasValidLicenses, purchase, purchaseLicenses]);

  const handleLicensesPurchase = useCallback(
    async (count: number) => {
      if (isPurchasingRef.current) return;
      isPurchasingRef.current = true;
      setLicensesPurchase("busy");

      const result = await purchase(count);
      // The page is leaving for Stripe: stay busy, so Continue cannot open a second checkout.
      if (result?.outcome === "redirecting") return;
      isPurchasingRef.current = false;
      if (result?.outcome === "conflict") {
        // The price or customer just changed: show the new price before Continue is offered again.
        refetchSummary();
        setLicensesPurchase({ failure: result.message });
      } else if (result?.outcome === "failed") {
        setLicensesPurchase({ failure: PURCHASE_LICENSES_COPY.purchaseFailed });
      } else {
        setLicensesPurchase("idle");
      }
    },
    [purchase, refetchSummary]
  );

  /**
   * Back from Stripe, a browser may restore this page from its back-forward cache exactly as it
   * was left: busy, with Cancel disabled and the dialog refusing to close.
   */
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      isPurchasingRef.current = false;
      setLicensesPurchase("idle");
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const handleStartTrial = useCallback(async () => {
    const result = await startTrial();
    if (result.outcome === "started") {
      await completeTrialStart();
    } else if (result.outcome === "needs-support") {
      openSupportModal(result);
    }
  }, [completeTrialStart, openSupportModal, startTrial]);

  // Stripe sent this user back and the purchase is not live yet (`ScribePurchaseReturn`).
  const { isFinishing: isFinishingPurchase, isLive: isPurchaseLive } =
    useScribePurchaseInProgress(isFlagOn);

  // A purchase that just went live makes checkout refuse ("already has an active
  // subscription"): read that now, so Purchase opens the support modal and not the dialog.
  useEffect(() => {
    if (isPurchaseLive) refetchSummary();
  }, [isPurchaseLive, refetchSummary]);

  /**
   * The buyer now holds a licence of their own: `ScribePurchaseReturn` read it once the purchase
   * went live. The page host checked access once, when it mounted, and may have checked before
   * the payment landed, so let it check again. A buyer who bought only for colleagues has no
   * licence, and the screen stays. Not during a trial start, which re-checks by itself.
   */
  const features = useAppSelector(selectFeatures);
  const hasOwnScribeLicense = Boolean(
    features?.[AMBIENT_SCRIBE_FEATURE_KEY]?.active
  );
  const hasRecheckedForLicenseRef = useRef(false);
  useEffect(() => {
    if (!isFlagOn || !hasOwnScribeLicense || trialStart !== "not-started") {
      return;
    }
    if (hasRecheckedForLicenseRef.current) return;
    hasRecheckedForLicenseRef.current = true;
    onTrialStarted?.();
  }, [hasOwnScribeLicense, isFlagOn, onTrialStarted, trialStart]);

  const openLicensesDialog = useCallback(() => {
    setLicensesPurchase("idle");
    setIsLicensesDialogOpen(true);
  }, []);

  // The dialog's own refetch can bring a refusal the prefetched summary did not have.
  useEffect(() => {
    if (!isLicensesDialogOpen || !purchaseRefusal || isPurchasingRef.current) {
      return;
    }
    setIsLicensesDialogOpen(false);
    openSupportModal({ message: purchaseRefusal });
  }, [isLicensesDialogOpen, openSupportModal, purchaseRefusal]);
  const hasStripeSetup = Boolean(currentPractice?.stripe_customer_id);
  /**
   * A checkout that charges at once needs the practice's billing (a Stripe customer), which only
   * Suno support can set up. Purchase Subscription stays a live button here: a click explains how
   * to buy, rather than a greyed-out button that does nothing. A refusal already read from the
   * purchase summary (flag on) wins over it in `handleCtaClick`; a click before that summary
   * lands still gets "How to purchase", which also sends the practice to support.
   */
  const needsBillingSetup =
    !isNoCardTrial && isPracticeResolved && chargesAtOnce && !hasStripeSetup;

  // The trial without a card needs no Stripe customer and no licence count, and starting it
  // does not read `feature`, so none of those can disable it. Nor does a checkout that comes with
  // a trial need a Stripe customer: without one the backend starts the trial without a card.
  // The "How to purchase" modal needs neither the price nor a licence count.
  const isCtaDisabled =
    isFinishingPurchase ||
    isRecheckingSupport ||
    (isNoCardTrial
      ? !isPracticeResolved ||
        Boolean(offerError) ||
        isStarting ||
        trialStart !== "not-started"
      : !isPracticeResolved ||
        trialStart !== "not-started" ||
        (!needsBillingSetup && (!feature || !hasValidLicenses)));

  const openPurchaseHelp = useCallback(() => {
    const practiceName = currentPractice?.name || undefined;
    openModal(
      COPY.purchaseHelpTitle,
      <PurchaseHelpContent
        price={hasPrice ? formatMoney(Number(unitPrice)) : undefined}
        practiceName={practiceName}
      />,
      { actionButtonText: COPY.purchaseHelpEmailAction },
      // A mailto hands off to the mail app; the page stays where it is.
      () => window.open(buildPurchaseHelpMailto(practiceName), "_self")
    );
  }, [currentPractice?.name, hasPrice, openModal, unitPrice]);

  // With the flag on the count is chosen in the dialog, which shows who each licence goes to.
  const proceedToCheckout = useCallback(
    () => (isFlagOn ? openLicensesDialog() : handlePurchase()),
    [handlePurchase, isFlagOn, openLicensesDialog]
  );

  /**
   * Both "contact Suno support" answers rest on data this screen read earlier: the refusal on the
   * summary it prefetched, the billing on the practice read at login. Suno support changes both
   * from another session (SN-19868) — and the refusal depends on the billing too (a lapsed staff
   * trial blocks only while the practice has no Stripe customer) — so re-read them before sending
   * anyone to support. Same order as before: checkout's refusal, then "How to purchase".
   */
  const recheckBeforeSupport = useCallback(async () => {
    const practiceId = currentPractice?.id;
    if (!practiceId || isRecheckingSupportRef.current) return;
    isRecheckingSupportRef.current = true;
    setIsRecheckingSupport(true);
    try {
      // RTK throws on refetching a skipped query, hence the `shouldReadSummary` guard.
      const [freshSummary, freshPractice] = await Promise.all([
        shouldReadSummary ? refetchSummaryQuery() : undefined,
        needsBillingSetup ? readStripeCustomer(practiceId) : undefined
      ]);
      // A failed refetch keeps the refusal already known: an unconfirmed refusal is not lifted.
      const refusal =
        freshSummary && !freshSummary.isError
          ? (freshSummary.data?.purchase_refusal ?? null)
          : purchaseRefusal;
      if (refusal) return openSupportModal({ message: refusal });
      if (needsBillingSetup) {
        const customerId = freshPractice?.isSuccess
          ? freshPractice.data?.stripe_customer_id
          : null;
        // No customer, or the read failed: what the screen said before this check existed.
        if (!customerId) return openPurchaseHelp();
        dispatch(updateCurrentPractice({ stripe_customer_id: customerId }));
      }
      return proceedToCheckout();
    } finally {
      isRecheckingSupportRef.current = false;
      setIsRecheckingSupport(false);
    }
  }, [
    currentPractice?.id,
    dispatch,
    needsBillingSetup,
    openPurchaseHelp,
    openSupportModal,
    proceedToCheckout,
    purchaseRefusal,
    readStripeCustomer,
    refetchSummaryQuery,
    shouldReadSummary
  ]);

  const handleCtaClick = () => {
    if (isNoCardTrial) return handleStartTrial();
    // Checkout would refuse, or billing isn't set up: both send the practice to support, so both
    // are re-read first. Without a Stripe customer the refusal still wins — billing set up or not,
    // buying here can't work, and the backend's sentence (e.g. "you don't have a license") is the
    // true reason.
    if (purchaseRefusal || needsBillingSetup) return recheckBeforeSupport();
    return proceedToCheckout();
  };

  /**
   * `null` while the practice is still resolving: the CTA is disabled but there is
   * nothing true to say yet. Rendered as visible text, not tooltip-only — a disabled
   * button whose only explanation is a hover is unreachable by touch.
   */
  const disabledReason = useMemo(() => {
    if (!isPracticeResolved) return null;
    // A started trial waits for the host to swap this screen out; `needs-reload`: nothing will.
    if (trialStart !== "not-started") {
      return trialStart === "needs-reload"
        ? COPY.reasonTrialStartedReload
        : null;
    }
    if (isFinishingPurchase) return COPY.reasonFinishingPurchase;
    if (isNoCardTrial) return offerError;
    // Not a reason: the button is live, and its modal says what to do.
    if (needsBillingSetup) return null;
    if (!feature) return COPY.reasonNoPricing;
    if (!hasValidLicenses) return COPY.reasonNoLicenses;
    return null;
  }, [
    feature,
    hasValidLicenses,
    isFinishingPurchase,
    isNoCardTrial,
    isPracticeResolved,
    needsBillingSetup,
    offerError,
    trialStart
  ]);

  if (isLoading || isLoadingSubscriptions || isLoadingOffer) {
    return <CircularProgress />;
  }

  const noCardTrial = {
    offer,
    trialDays,
    isNoCardTrial,
    chargesAtOnce
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "clamp(24px, 3vw, 40px)",
        width: "100%",
        maxWidth: `${MAX_CONTENT_WIDTH}px`,
        padding: "clamp(16px, 0vw, 28px) clamp(16px, 3vw, 40px)",
        boxSizing: "border-box"
      }}
    >
      {showTrialStatusBanner && (
        <TrialStatusBanner featureKey={AMBIENT_SCRIBE_FEATURE_KEY} />
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: HERO_GRID,
          gap: "clamp(24px, 3.5vw, 48px)",
          alignItems: "start"
        }}
      >
        <Stack sx={{ gap: 2.5, maxWidth: "680px", minWidth: 0 }}>
          <Stack
            direction="row"
            useFlexGap
            sx={{ flexWrap: "wrap", alignItems: "center", gap: 1 }}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                gap: 1,
                background: misc.mutedPurple,
                border: `1px solid ${neutral[300]}`,
                borderRadius: "20px",
                padding: "4px 14px",
                color: misc.suno
              }}
            >
              <GraphicEqIcon aria-hidden sx={{ fontSize: fluidType(15, 17) }} />
              <Typography
                component="span"
                sx={{ ...UPPERCASE_LABEL_SX, color: "inherit" }}
              >
                {COPY.eyebrow}
              </Typography>
            </Stack>
            {offer === "start" && trialDays ? (
              <Stack
                direction="row"
                sx={{
                  alignItems: "center",
                  gap: 1,
                  // sunoGreen on lightGreen measures 4.96:1.
                  background: misc.lightGreen,
                  borderRadius: "20px",
                  padding: "4px 14px",
                  color: misc.sunoGreen
                }}
              >
                <CreditCardOffOutlinedIcon
                  aria-hidden
                  sx={{ fontSize: fluidType(15, 17) }}
                />
                <Typography
                  component="span"
                  sx={{ ...UPPERCASE_LABEL_SX, color: "inherit" }}
                >
                  {COPY.heroBadge(trialDays)}
                </Typography>
              </Stack>
            ) : null}
          </Stack>

          <Typography
            component="h2"
            sx={{
              fontSize: fluidType(28, 46),
              lineHeight: 1.1,
              fontWeight: 800,
              letterSpacing: "-0.9px",
              color: misc.sunoGreen,
              mt: -1
            }}
          >
            {COPY.heading}
          </Typography>

          <Typography
            sx={{
              fontSize: fluidType(17, 22),
              lineHeight: 1.45,
              fontWeight: 600,
              color: neutral[800]
            }}
          >
            {COPY.subheading}
          </Typography>

          <Typography
            sx={{
              fontSize: fluidType(15, 17.5),
              lineHeight: 1.6,
              color: neutral[800],
              mt: -1
            }}
          >
            {COPY.body}
          </Typography>

          <Box
            component="ul"
            sx={{
              display: "grid",
              gap: 1.0,
              listStyle: "none",
              margin: 0,
              padding: 0
            }}
          >
            {AMBIENT_SCRIBE_PLACEHOLDER_BULLETS.map((bullet) => (
              <Box
                component="li"
                key={bullet}
                sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
              >
                <CheckIcon
                  aria-hidden
                  sx={{
                    fontSize: fluidType(20, 24),
                    color: misc.sunoGreen,
                    flexShrink: 0,
                    mt: "1px"
                  }}
                />
                <Typography
                  sx={{
                    fontSize: fluidType(15, 17.5),
                    lineHeight: 1.4,
                    fontWeight: 700,
                    color: neutral[900]
                  }}
                >
                  {bullet}
                </Typography>
              </Box>
            ))}
          </Box>
        </Stack>

        <Stack sx={{ gap: 1.75, minWidth: 0 }}>
          <Typography component="p" sx={UPPERCASE_LABEL_SX}>
            {COPY.videoLabel}
          </Typography>
          <Box
            sx={{
              border: `1px solid ${neutral[300]}`,
              borderRadius: "8px",
              overflow: "hidden",
              background: common.white,
              boxShadow: theme.shadows[1]
            }}
          >
            <Box
              sx={{
                position: "relative",
                paddingBottom: "56.25%",
                height: 0,
                // Not white: a blocked or slow embed reads as a deliberate letterbox
                // rather than a broken layout.
                background: misc.chatPlum
              }}
            >
              <iframe
                src={AMBIENT_SCRIBE_DEMO_EMBED_URL}
                title={COPY.videoIframeTitle}
                loading="lazy"
                allowFullScreen
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: 0
                }}
              />
            </Box>
            <Box
              sx={{
                borderTop: `1px solid ${neutral[300]}`,
                padding: "12px 16px"
              }}
            >
              {/* A real link, so the demo survives a clinic network that blocks
                  loom.com in an iframe but allows a normal tab. */}
              <Link
                href={AMBIENT_SCRIBE_DEMO_SHARE_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: fluidType(14, 15), fontWeight: 700 }}
              >
                {COPY.videoCaption}
              </Link>
            </Box>
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: offer === "start" ? TRIAL_BAND_GRID : BAND_GRID,
          gap: "clamp(20px, 1vw, 40px)",
          alignItems: "center",
          background: common.white,
          border: `1px solid ${neutral[300]}`,
          borderRadius: "8px",
          boxShadow: theme.shadows[1],
          padding: "clamp(16px, 0vw, 28px)",
          mt: -1
        }}
      >
        <Typography
          component="div"
          sx={{
            fontSize: fluidType(15, 17.5),
            lineHeight: 1.6,
            color: neutral[800],
            // The sentence measures ~150ch, so a one-column band laid it out as one ~1450px line.
            // 80ch is a readable measure and keeps it to two lines at any fluid size.
            maxWidth: "80ch"
          }}
        >
          {trialBandLeadIn(noCardTrial)}
          {COPY.trialBand}
        </Typography>
        <Stack sx={{ gap: 2, minWidth: 0 }}>
          {/* With a trial on offer the row wraps, so a narrow host (the dialog, the patient tab,
              a phone) drops the no-card note under the CTA rather than squeezing three items.
              The card checkout's row must not wrap: in the two-up band it fits only by
              shrinking a little, and wrapping would put the price under the CTA. */}
          <Stack
            direction="row"
            useFlexGap
            sx={{
              alignItems: "flex-start",
              flexWrap: offer === "start" ? "wrap" : "nowrap",
              gap: 2
            }}
          >
            <Tooltip
              title={disabledReason ?? ""}
              arrow
              disableInteractive
              placement="top"
              {...(nestedLayerZIndex !== undefined && {
                PopperProps: { sx: { zIndex: nestedLayerZIndex } }
              })}
            >
              <Box component="span" sx={{ maxWidth: "100%" }}>
                <LoadingButton
                  variant="contained"
                  sx={{
                    ...glowShadowGreenButtonSx,
                    // The label is 28 characters. At a fixed 20px inside a nowrap span it
                    // is ~390px wide and overflows a 375px viewport, so the size is fluid
                    // and the text is allowed to wrap.
                    fontSize: fluidType(16, 21),
                    lineHeight: 1.25,
                    minHeight: "110px",
                    maxWidth: "100%",
                    whiteSpace: "normal",
                    // The glow's gradient is `!important`, so MUI's disabled style only greyed
                    // the label, on a button that still looked live. Disabled now reads as
                    // disabled: the whole button dimmed, its label still white, and no glow.
                    // Not while loading, where the label is hidden under the spinner.
                    "&.Mui-disabled:not(.MuiLoadingButton-loading)": {
                      color: common.white,
                      opacity: 0.5,
                      boxShadow: "none"
                    },
                    "& .MuiLoadingButton-loadingIndicator": {
                      color: common.white
                    }
                  }}
                  disabled={isCtaDisabled}
                  endIcon={<NavigateNextIcon />}
                  loading={
                    isNoCardTrial
                      ? isStarting
                      : isCreatingFeatureSubscription || isRecheckingSupport
                  }
                  onClick={handleCtaClick}
                >
                  {ctaLabel(noCardTrial)}
                </LoadingButton>
              </Box>
            </Tooltip>

            {isNoCardTrial && hasPrice && (
              <Box sx={PRICE_BOX_SX}>
                <Typography sx={FIELD_LABEL_SX}>{COPY.priceLabel}</Typography>
                <Typography
                  sx={{
                    mt: 1,
                    fontSize: fluidType(19, 22),
                    fontWeight: 700,
                    color: misc.sunoGreen
                  }}
                >
                  {formatMoney(Number(unitPrice))}
                </Typography>
                {/* Not on a refusal: there is no trial for the price to follow. */}
                {offer !== null && (
                  <Typography
                    sx={{
                      fontSize: fluidType(14, 15),
                      lineHeight: 1.4,
                      color: neutral[800]
                    }}
                  >
                    {COPY.priceAfterTrial}
                  </Typography>
                )}
              </Box>
            )}

            {!isNoCardTrial && !isFlagOn && (
              <Box
                sx={{
                  background: "rgba(15, 136, 202, 0.07)",
                  borderRadius: "8px",
                  padding: "8px 16px"
                }}
              >
                <Typography
                  component="span"
                  id={LICENSES_INPUT_LABEL_ID}
                  sx={{ ...FIELD_LABEL_SX, display: "block" }}
                >
                  {COPY.licensesLabel}
                </Typography>
                <TextField
                  type="number"
                  value={licenses}
                  disabled={needsBillingSetup}
                  onChange={(event) => setLicenses(event.target.value)}
                  onBlur={handleLicensesBlur}
                  // A scroll gesture over a focused number input changes its value in
                  // Chrome — an order quantity must not be altered by scrolling past it.
                  onWheel={handleLicensesWheel}
                  inputProps={{
                    min: MIN_LICENSES,
                    max: MAX_LICENSES,
                    inputMode: "numeric",
                    "aria-labelledby": LICENSES_INPUT_LABEL_ID
                  }}
                  sx={{
                    mt: 1,
                    "& .MuiOutlinedInput-root": {
                      "& input": {
                        color: misc.sunoGreen,
                        fontSize: fluidType(19, 22),
                        padding: "4px 8px",
                        width: "78px"
                      }
                    }
                  }}
                />
              </Box>
            )}

            {!isNoCardTrial && hasPrice && (
              <Box sx={PRICE_BOX_SX}>
                <Typography sx={FIELD_LABEL_SX}>{COPY.priceLabel}</Typography>
                <Typography
                  sx={{
                    mt: 1,
                    fontSize: fluidType(19, 22),
                    fontWeight: 700,
                    color: misc.sunoGreen
                  }}
                >
                  {hasValidLicenses
                    ? formatMoney(purchaseLicenses * Number(unitPrice))
                    : COPY.priceUnavailable}
                </Typography>
                {isFlagOn && (
                  <Typography
                    sx={{
                      fontSize: fluidType(14, 15),
                      lineHeight: 1.4,
                      color: neutral[800]
                    }}
                  >
                    {COPY.priceForOneLicense}
                  </Typography>
                )}
              </Box>
            )}

            {offer === "start" && (
              // The top padding is the price box's own, so the two first lines align.
              <Stack
                direction="row"
                sx={{ gap: 1, alignItems: "flex-start", pt: 1 }}
              >
                <CreditCardOffOutlinedIcon
                  aria-hidden
                  sx={{
                    fontSize: fluidType(20, 22),
                    color: misc.sunoGreen,
                    flexShrink: 0,
                    mt: "2px"
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: fluidType(15, 17),
                      fontWeight: 700,
                      lineHeight: 1.5,
                      color: neutral[900]
                    }}
                  >
                    {COPY.noCardRequired}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: fluidType(14, 16),
                      lineHeight: 1.5,
                      color: neutral[800]
                    }}
                  >
                    {COPY.nothingChargedAtTrialEnd}
                  </Typography>
                </Box>
              </Stack>
            )}
          </Stack>

          {Boolean(disabledReason) && (
            <Typography
              sx={{
                fontSize: fluidType(14, 16),
                lineHeight: 1.5,
                color: neutral[800]
              }}
            >
              {disabledReason}
            </Typography>
          )}
        </Stack>
      </Box>

      {isLicensesDialogOpen && feature && (
        <PurchaseLicensesDialog
          feature={feature}
          nestedLayerZIndex={nestedLayerZIndex}
          isPurchasing={licensesPurchase === "busy"}
          purchaseError={
            typeof licensesPurchase === "object"
              ? licensesPurchase.failure
              : null
          }
          onClose={() => setIsLicensesDialogOpen(false)}
          onPurchase={handleLicensesPurchase}
        />
      )}
    </Box>
  );
}
